"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { API_BASE_URL } from "@lib/config";
import { useIntersection } from "@lib/hooks/use-in-view";
import { getSearchContext } from "@lib/search/session";
import { sendSearchEvents } from "@lib/search/events";
import { normalizeSelectableCountryCode, resolveClientCountrySelection } from "@lib/storefront-selection";
import ProductPreview from "@modules/products/components/product-preview";
import { SortOptions } from "@modules/store/components/refinement-list/sort-products";
import { shouldAutoFetchInfiniteProducts, shouldLoadNextInfiniteProductsPage } from "./infinite-products.logic";
const DEFAULT_LIMIT = 16;
type ProductHit = any;
type BrowseResponse = {
  items?: ProductHit[];
  products?: ProductHit[];
  count?: number;
  nextCursor?: string | null;
  hasMore?: boolean;
  brand?: {
    slug?: string | null;
    name?: string | null;
  } | null;
};
const normalizeListParam = (params: URLSearchParams, key: string) => {
  const all = params.getAll(key);
  if (all.length) return all;
  const raw = params.get(key);
  if (!raw) return [];
  return raw.split(",").map(value => value.trim()).filter(Boolean);
};
const normalizeIdentityToken = (value: unknown): string => {
  if (typeof value === "string") return value.trim().toLowerCase();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
};
const resolveProductIdentity = (product: ProductHit): string | null => {
  const idCandidates = [product?._id, product?.id, product?.sourceId];
  for (const candidate of idCandidates) {
    const normalized = normalizeIdentityToken(candidate);
    if (normalized) return `id:${normalized}`;
  }
  const vendor = normalizeIdentityToken(product?.vendorUid || product?.brand?.slug || product?.brandSlug || product?.brand?.name);
  const slug = normalizeIdentityToken(product?.slug);
  if (vendor && slug) return `vendor:${vendor}:slug:${slug}`;
  if (slug) return `slug:${slug}`;
  const name = normalizeIdentityToken(product?.name || product?.title);
  if (vendor && name) return `vendor:${vendor}:name:${name}`;
  return null;
};
const resolveProductFallbackFingerprint = (product: ProductHit): string => {
  const vendor = normalizeIdentityToken(product?.vendorUid || product?.brand?.slug || product?.brandSlug || product?.brand?.name);
  const slug = normalizeIdentityToken(product?.slug);
  const name = normalizeIdentityToken(product?.name || product?.title);
  const image = normalizeIdentityToken(product?.thumbnail || product?.imageUrl || product?.images?.[0]?.url);
  return [vendor, slug, name, image].join("|");
};
const dedupeIncomingProducts = (products: ProductHit[]): ProductHit[] => {
  const seen = new Set<string>();
  const result: ProductHit[] = [];
  products.forEach(product => {
    const identity = resolveProductIdentity(product);
    if (!identity) {
      result.push(product);
      return;
    }
    if (seen.has(identity)) return;
    seen.add(identity);
    result.push(product);
  });
  return result;
};
const mergeProducts = (existing: ProductHit[], incoming: ProductHit[]): {
  merged: ProductHit[];
  appendedItems: ProductHit[];
} => {
  const merged = [...existing];
  const identitySeen = new Set<string>();
  const fallbackSeen = new Set<string>();
  existing.forEach(product => {
    const identity = resolveProductIdentity(product);
    if (identity) {
      identitySeen.add(identity);
      return;
    }
    fallbackSeen.add(resolveProductFallbackFingerprint(product));
  });
  const appendedItems: ProductHit[] = [];
  incoming.forEach(product => {
    const identity = resolveProductIdentity(product);
    if (identity) {
      if (identitySeen.has(identity)) return;
      identitySeen.add(identity);
      merged.push(product);
      appendedItems.push(product);
      return;
    }
    const fallback = resolveProductFallbackFingerprint(product);
    if (fallbackSeen.has(fallback)) return;
    fallbackSeen.add(fallback);
    merged.push(product);
    appendedItems.push(product);
  });
  return {
    merged,
    appendedItems
  };
};
const resolveProductRenderKey = (product: ProductHit, index: number): string => {
  const identity = resolveProductIdentity(product);
  if (identity) return identity;
  const fallback = resolveProductFallbackFingerprint(product) || "unknown";
  return `row:${index}:${fallback}`;
};
const applyBrandFallbackToItems = (products: ProductHit[], brand: BrowseResponse["brand"]): ProductHit[] => {
  const brandSlug = typeof brand?.slug === "string" ? brand.slug.trim() : "";
  if (!brandSlug) return products;
  const brandName = typeof brand?.name === "string" ? brand.name.trim() : "";
  return products.map(product => {
    const itemBrandSlug = typeof product?.brand?.slug === "string" ? product.brand.slug.trim() : typeof product?.brandSlug === "string" ? product.brandSlug.trim() : "";
    if (itemBrandSlug) return product;
    const nextBrand = product?.brand && typeof product.brand === "object" ? {
      ...product.brand,
      slug: brandSlug,
      name: typeof product.brand?.name === "string" && product.brand.name.trim() ? product.brand.name : brandName || undefined
    } : {
      slug: brandSlug,
      ...(brandName ? {
        name: brandName
      } : {})
    };
    return {
      ...product,
      brandSlug: brandSlug,
      brand: nextBrand
    };
  });
};
export default function InfiniteProducts({
  endpoint = "/public/products",
  baseParams,
  pageContext,
  region,
  defaultSort = "best_selling",
  limit = DEFAULT_LIMIT,
  initialItems,
  initialHasMore = false,
  initialNextCursor = null,
  disableAutoFetch = false
}: {
  endpoint?: string;
  baseParams?: Record<string, string | string[] | undefined>;
  pageContext: "shop_page" | "category_page" | "brand_page" | "collection_page";
  region: any;
  defaultSort?: SortOptions;
  limit?: number;
  initialItems?: ProductHit[];
  initialHasMore?: boolean;
  initialNextCursor?: string | null;
  disableAutoFetch?: boolean;
}) {
  const searchParams = useSearchParams();
  const [selectedCountry, setSelectedCountry] = useState<string>(() => resolveClientCountrySelection());
  const {
    sortBy,
    selectedBrands,
    selectedCategories,
    selectedCollections,
    selectedColors,
    selectedSizes,
    selectedMaterials,
    selectedAvailability,
    selectedCurrency,
    inStock,
    discounted,
    priceMinParam,
    priceMaxParam
  } = useMemo(() => {
    return {
      sortBy: searchParams.get("sortBy") as SortOptions || defaultSort,
      selectedBrands: normalizeListParam(searchParams, "brand"),
      selectedCategories: normalizeListParam(searchParams, "category"),
      selectedCollections: normalizeListParam(searchParams, "collection"),
      selectedColors: normalizeListParam(searchParams, "color"),
      selectedSizes: normalizeListParam(searchParams, "size"),
      selectedMaterials: normalizeListParam(searchParams, "material"),
      selectedAvailability: normalizeListParam(searchParams, "availabilityType"),
      selectedCurrency: normalizeListParam(searchParams, "currency"),
      inStock: searchParams.get("inStock") === "true",
      discounted: searchParams.get("discounted") === "true",
      priceMinParam: searchParams.get("priceMin") || searchParams.get("minPrice"),
      priceMaxParam: searchParams.get("priceMax") || searchParams.get("maxPrice")
    };
  }, [defaultSort, searchParams]);
  const requestKey = useMemo(() => {
    return JSON.stringify({
      sortBy,
      selectedBrands,
      selectedCategories,
      selectedCollections,
      selectedColors,
      selectedSizes,
      selectedMaterials,
      selectedAvailability,
      selectedCurrency,
      inStock,
      discounted,
      priceMinParam,
      priceMaxParam,
      baseParams,
      selectedCountry
    });
  }, [baseParams, discounted, inStock, priceMaxParam, priceMinParam, selectedAvailability, selectedBrands, selectedCategories, selectedCollections, selectedColors, selectedCurrency, selectedMaterials, selectedSizes, selectedCountry, sortBy]);
  const [items, setItems] = useState<ProductHit[]>(() => Array.isArray(initialItems) ? initialItems : []);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(Boolean(initialHasMore));
  const [nextCursor, setNextCursor] = useState<string | null>(initialNextCursor || null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const isSentinelVisible = useIntersection(sentinelRef, "200px");
  const abortRef = useRef<AbortController | null>(null);
  const itemsRef = useRef<ProductHit[]>([]);
  const paginationModeRef = useRef<"cursor" | "offset">("cursor");
  const pageRef = useRef(1);
  const requestIdRef = useRef(0);
  const noProgressStreakRef = useRef(0);
  const loadPageRef = useRef<({
    cursor,
    replace
  }: {
    cursor?: string | null;
    replace?: boolean;
  }) => Promise<void>>(async () => {});
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);
  const loadPage = useCallback(async ({
    cursor,
    replace
  }: {
    cursor?: string | null;
    replace?: boolean;
  }) => {
    if (!shouldAutoFetchInfiniteProducts(disableAutoFetch)) {
      return;
    }
    const requestId = ++requestIdRef.current;
    if (replace) {
      setLoading(true);
      setLoadingMore(false);
    } else {
      setLoadingMore(true);
    }
    setError(null);
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const url = new URL(`${API_BASE_URL}${endpoint}`);
      url.searchParams.set("limit", String(limit));
      url.searchParams.set("sort", sortBy);
      url.searchParams.set("countryCode", selectedCountry);
      if (paginationModeRef.current === "cursor") {
        if (cursor) {
          url.searchParams.set("cursor", cursor);
        }
      } else {
        url.searchParams.set("page", String(pageRef.current));
      }
      if (baseParams) {
        Object.entries(baseParams).forEach(([key, value]) => {
          if (!value) return;
          if (Array.isArray(value)) {
            value.forEach(entry => url.searchParams.append(key, entry));
            return;
          }
          url.searchParams.set(key, value);
        });
      }
      selectedBrands.forEach(value_0 => url.searchParams.append("brand", value_0));
      selectedCategories.forEach(value_1 => url.searchParams.append("category", value_1));
      selectedCollections.forEach(value_2 => url.searchParams.append("collection", value_2));
      selectedColors.forEach(value_3 => url.searchParams.append("color", value_3));
      selectedSizes.forEach(value_4 => url.searchParams.append("size", value_4));
      selectedMaterials.forEach(value_5 => url.searchParams.append("material", value_5));
      selectedAvailability.forEach(value_6 => url.searchParams.append("availabilityType", value_6));
      selectedCurrency.forEach(value_7 => url.searchParams.append("currency", value_7));
      if (inStock) url.searchParams.set("inStock", "true");
      if (discounted) url.searchParams.set("discounted", "true");
      if (priceMinParam) url.searchParams.set("priceMin", priceMinParam);
      if (priceMaxParam) url.searchParams.set("priceMax", priceMaxParam);
      const response = await fetch(url.toString(), {
        signal: controller.signal
      });
      if (!response.ok) {
        throw new Error(`Browse failed with status ${response.status}`);
      }
      const payload: BrowseResponse = await response.json();
      if (requestId !== requestIdRef.current) {
        return;
      }
      const cursorItems = Array.isArray(payload?.items) ? payload.items : null;
      const incomingItemsRaw = cursorItems ?? (Array.isArray(payload?.products) ? payload.products : []);
      const incomingItems = dedupeIncomingProducts(applyBrandFallbackToItems(incomingItemsRaw, payload?.brand || null));
      const cursorMode = Array.isArray(payload?.items);
      const loadedBefore = replace ? 0 : itemsRef.current.length;
      const previousItems = replace ? [] : itemsRef.current;
      const {
        merged,
        appendedItems
      } = mergeProducts(previousItems, incomingItems);
      setItems(merged);
      itemsRef.current = merged;
      const appendedCount = appendedItems.length;
      if (replace) {
        noProgressStreakRef.current = 0;
      } else if (appendedCount === 0) {
        noProgressStreakRef.current += 1;
      } else {
        noProgressStreakRef.current = 0;
      }
      if (cursorMode) {
        paginationModeRef.current = "cursor";
        const nextCursorValue = payload?.nextCursor || null;
        const cursorDidNotAdvance = Boolean(cursor) && Boolean(nextCursorValue) && String(cursor) === String(nextCursorValue);
        const stopForNoProgress = noProgressStreakRef.current >= 2;
        const canContinue = Boolean(payload?.hasMore) && Boolean(nextCursorValue) && !cursorDidNotAdvance && !stopForNoProgress;
        setNextCursor(canContinue ? nextCursorValue : null);
        setHasMore(canContinue);
      } else {
        paginationModeRef.current = "offset";
        const count = typeof payload?.count === "number" ? payload.count : incomingItems.length;
        const totalLoaded = merged.length;
        const canContinue_0 = totalLoaded < count && (replace || appendedCount > 0);
        setHasMore(canContinue_0);
        setNextCursor(null);
        if (canContinue_0) {
          pageRef.current = replace ? 2 : pageRef.current + 1;
        }
      }
      if (appendedItems.length) {
        const {
          sessionId
        } = getSearchContext();
        const productIds = appendedItems.map(item => String(item._id || item.id || "")).filter(Boolean);
        const positions = productIds.map((_, index) => loadedBefore + index + 1);
        await sendSearchEvents({
          impressions: [{
            sessionId,
            query: null,
            pageContext,
            productIds,
            visiblePositions: positions,
            isSponsored: false
          }]
        });
      }
    } catch (err) {
      if ((err as any)?.name === "AbortError") return;
      if (requestId !== requestIdRef.current) return;
      console.error("Failed to load products", err);
      setError("No pudimos cargar los productos. Intenta nuevamente.");
      setHasMore(false);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [baseParams, disableAutoFetch, discounted, endpoint, inStock, limit, pageContext, priceMaxParam, priceMinParam, selectedAvailability, selectedBrands, selectedCategories, selectedCollections, selectedColors, selectedCurrency, selectedMaterials, selectedSizes, selectedCountry, sortBy]);
  useEffect(() => {
    loadPageRef.current = loadPage;
  }, [loadPage]);
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{
        country?: string;
      }>).detail;
      const nextCountry = normalizeSelectableCountryCode(detail?.country);
      if (!nextCountry) return;
      setSelectedCountry(nextCountry);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("lealti:country", handler as EventListener);
      return () => window.removeEventListener("lealti:country", handler as EventListener);
    }
    return undefined;
  }, []);
  useEffect(() => {
    if (shouldAutoFetchInfiniteProducts(disableAutoFetch)) {
      return;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    pageRef.current = 1;
    paginationModeRef.current = "cursor";
    noProgressStreakRef.current = 0;
    const nextItems = Array.isArray(initialItems) ? initialItems : [];
    itemsRef.current = nextItems;
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setLoading(false);
      setLoadingMore(false);
      setError(null);
      setItems(nextItems);
      setHasMore(Boolean(initialHasMore));
      setNextCursor(initialNextCursor || null);
    });
    return () => {
      cancelled = true;
    };
  }, [disableAutoFetch, initialHasMore, initialItems, initialNextCursor]);
  useEffect(() => {
    if (!shouldAutoFetchInfiniteProducts(disableAutoFetch)) {
      return;
    }
    pageRef.current = 1;
    paginationModeRef.current = "cursor";
    noProgressStreakRef.current = 0;
    let cancelled_0 = false;
    queueMicrotask(() => {
      if (cancelled_0) return;
      setNextCursor(null);
      setHasMore(false);
      setItems([]);
      void loadPageRef.current({
        replace: true
      });
    });
    return () => {
      cancelled_0 = true;
    };
  }, [disableAutoFetch, requestKey]);
  useEffect(() => {
    if (shouldLoadNextInfiniteProductsPage({
      disableAutoFetch,
      isSentinelVisible,
      hasMore,
      loading,
      loadingMore
    })) {
      void loadPageRef.current({
        cursor: nextCursor,
        replace: false
      });
    }
  }, [disableAutoFetch, hasMore, isSentinelVisible, loading, loadingMore, nextCursor]);
  return <div>
      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>}
      <ul className="grid grid-cols-2 w-full small:grid-cols-3 medium:grid-cols-4 gap-x-6 gap-y-8" data-testid="products-list">
        {items.map((p: any, index_0) => <li key={resolveProductRenderKey(p, index_0)}>
            <ProductPreview product={p} region={region} imagePriority={index_0 < 4} />
          </li>)}
      </ul>
      {loading && <div className="mt-6 grid grid-cols-2 gap-6 small:grid-cols-3 medium:grid-cols-4">
          {Array.from({
        length: 8
      }).map((__0, index_1) => <div key={index_1} className="aspect-[3/4] w-full rounded bg-neutral-200 animate-pulse" />)}
        </div>}
      {loadingMore && <div className="mt-6 text-center text-sm text-gray-500">Cargando más productos...</div>}
      <div ref={sentinelRef} className="h-4" />
    </div>;
}